// <s-cart-select> — lets shoppers tick which cart lines go to checkout. All ticked: the normal
// checkout runs. Some unticked: checkout opens with only the ticked items (cart permalink).
// Unticked lines are remembered per line key so cart re-renders keep the choice.
const STORAGE_KEY = 'done-cart-unselected';

class SCartSelect extends HTMLElement {
  #controller = null;
  #observer = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const signal = this.#controller.signal;
    document.addEventListener('change', (event) => {
      if (!event.target.matches?.('[data-cart-select]')) return;
      this.#save();
      this.#refresh();
    }, { signal });
    document.addEventListener('click', (event) => {
      const button = event.target.closest?.('button[name="checkout"], input[name="checkout"]');
      if (button) this.#checkout(event, button);
    }, { signal, capture: true });
    this.#observer = new MutationObserver(() => this.#refresh());
    this.#observer.observe(document.body, { childList: true, subtree: true });
    this.#refresh();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  #unselected() {
    try { return new Set(JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]')); } catch { return new Set(); }
  }

  #boxes(scope = document) {
    return Array.from(scope.querySelectorAll('[data-cart-select]'));
  }

  #save() {
    const unselected = this.#unselected();
    for (const box of this.#boxes()) {
      if (box.checked) unselected.delete(box.dataset.lineKey);
      else unselected.add(box.dataset.lineKey);
    }
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...unselected])); } catch {}
  }

  #money(cents) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  #refresh() {
    this.#observer?.disconnect();
    const unselected = this.#unselected();
    for (const box of this.#boxes()) {
      const shouldCheck = !unselected.has(box.dataset.lineKey);
      if (box.checked !== shouldCheck) box.checked = shouldCheck;
      box.closest('[style*="view-transition-name"]')?.classList.toggle('opacity-50', !shouldCheck);
    }
    for (const button of document.querySelectorAll('button[name="checkout"]')) {
      const dialog = button.closest('dialog');
      const boxes = this.#boxes(dialog || document.querySelector('main') || document);
      let note = button.parentElement.querySelector('[data-cart-select-note]');
      if (!note) {
        note = document.createElement('p');
        note.dataset.cartSelectNote = '';
        note.className = 'm-0 mt-2 text-center text-xs font-medium text-[#305c69]';
        note.setAttribute('aria-live', 'polite');
        button.insertAdjacentElement('afterend', note);
      }
      const picked = boxes.filter((b) => b.checked);
      const partial = boxes.length > 0 && picked.length < boxes.length;
      const total = picked.reduce((sum, b) => sum + Number(b.dataset.linePrice || 0), 0);
      note.hidden = !partial;
      note.textContent = partial
        ? (picked.length ? this.dataset.selectedLabel.replace('[count]', picked.length).replace('[total]', this.#money(total)) : this.dataset.noneLabel)
        : '';
      button.disabled = boxes.length > 0 && picked.length === 0;
    }
    this.#observer?.observe(document.body, { childList: true, subtree: true });
  }

  #checkout(event, button) {
    const dialog = button.closest('dialog');
    const boxes = this.#boxes(dialog || document.querySelector('main') || document);
    const picked = boxes.filter((b) => b.checked);
    if (!boxes.length || picked.length === boxes.length) return;
    event.preventDefault();
    event.stopPropagation();
    if (!picked.length) return;
    const items = picked.map((b) => `${b.value}:${b.dataset.qty}`).join(',');
    window.location.href = `${window.Shopify?.routes?.root || '/'}cart/${items}`;
  }
}

if (!customElements.get('s-cart-select')) customElements.define('s-cart-select', SCartSelect);
